import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  styled,
  Toolbar,
  Tooltip,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountCircle,
  AddShoppingCart,
  Inbox,
  Login,
  Logout,
  ManageAccounts,
  ReceiptLong,
  Settings,
  Menu as MenuIcon, // 추가
  ViewStream,
  ChevronRight as ChevronRightIcon,
  Article, // 문서 관련 아이콘 추가
  Description, // 문서 관련 추가 아이콘
  Assignment, // 과제/할일 관련 아이콘
  FolderOpen,
  OpenInBrowser, // 폴더 관련 아이콘
  Apps,
  BorderLeft,
  BorderTop,
  Phone,
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // 추가
// import NavigationGuard from './guard/NavigationGuard';
import * as sessionActions from '../../store/session';
import * as securityActions from '../../store/security';
import * as menuActions from '../../store/menu';
import * as layoutActions from '../../store/layout';
import { getMobileMenus, collectExpandedMenuIds } from '../../utils/menuUtils';
import { shouldShowSendBasket } from '../../utils/subMatCompany';
import CascadingHoverMenus from './CascadingHoverMenus';
import MenuTitleWithBadge from './MenuTitleWithBadge';
import SendBasketDialog from './SendBasketDialog';
import PaymentHistoryDialog from './PaymentHistoryDialog';
import PromotionDialog from '../../PromotionDialog';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: 5,
    top: 13,
    // border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    color: "white",
    // backgroundColor: "#9e9e9e",
    backgroundColor: "#2EA2DE", // 빨간색으로 변경
  },
}));

const useMenuLayout = () => {
  const menuLayout = useSelector(state => state.layout?.menuLayout);
  console.log("menuLayout", menuLayout);
  const dispatch = useDispatch();

  const setMenuLayout = (layout) => {
    if (layout) {
      dispatch(layoutActions.setMenuLayout(layout));
      localStorage.setItem('menuLayout', layout); // 로컬 스토리지에 저장
    }
  };

  return [menuLayout, setMenuLayout];
};

const Navigation = ({ isLoaded, menus }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sessionUser = useSelector(state => state.session.sessionUser);
  const error = useSelector(state => state.error.error);
  const gsendBaskets = useSelector(state => state.gsendBasket.gsendBaskets);
  const selectedMenuInfo = useSelector(state => state.menu.selectedMenuInfo);
  const showSendBasket = shouldShowSendBasket(sessionUser);
  // 회원사 로그인은 항상, 사용자 로그인은 관리자 위임(Users.role)된 경우만. sessionUser.role은 사용자 로그인에서 항상 USER_ROLE이다.
  const showPaymentHistory = sessionUser?.sessionUserLoginType !== 'USER' || sessionUser?.user?.role === 'ADMIN_ROLE';

  const [openBasket, setOpenBasket] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openPaymentHistory, setOpenPaymentHistory] = useState(false);  
  const [mobileOpen, setMobileOpen] = useState(false); // 추가
  // 펼쳐진 메뉴 상태 관리를 위한 state 추가
  const [expandedMenus, setExpandedMenus] = useState({});
  const [menuLayout, setMenuLayout] = useMenuLayout();
  const [openPromotion, setOpenPromotion] = useState(false);

  // 화면 탭·라우트 변경 시 해당 메뉴 경로를 좌측/모바일 메뉴에서 펼침
  useEffect(() => {
    if (!menus?.length) return;
    const href = selectedMenuInfo?.menu?.href || location.pathname;
    if (!href) return;
    const next = {};
    const desktopIds = collectExpandedMenuIds(menus, href);
    (desktopIds || []).forEach((id) => { next[id] = true; });
    if (sessionUser) {
      const mobileIds = collectExpandedMenuIds(getMobileMenus(menus, sessionUser), href);
      (mobileIds || []).forEach((id) => { next[id] = true; });
    }
    if (Object.keys(next).length === 0) return;
    setExpandedMenus((prev) => ({ ...prev, ...next }));
  }, [location.pathname, menus, sessionUser, selectedMenuInfo?.menu?.href]);

  // 모바일 Drawer 열릴 때 상위 메뉴(서류관리, 이력관리) 자동 펼침
  useEffect(() => {
    if (mobileOpen && menus) {
      const mobileMenus = getMobileMenus(menus, sessionUser);
      const idsToExpand = mobileMenus
        .filter((m) => m.submenus?.length > 0)
        .map((m, i) => `0-${i}-${m.title}`);
      setExpandedMenus((prev) => {
        const next = { ...prev };
        idsToExpand.forEach((id) => { next[id] = true; });
        return next;
      });
    }
  }, [mobileOpen, menus]);

  // const selectSecurityOpenGClientList = (service, gclientId) => dispatch(securityActions.selectSecurityOpenGClientList(service, gclientId))
  // const selectSecurityCloseGClientList = (service, gclientId) => dispatch(securityActions.selectSecurityCloseGClientList(service, gclientId))
  // const selectOpenedAboutMeGClientList = (service, gclientId) => dispatch(securityActions.selectOpenedAboutMeGClientList(service, gclientId))

  const handleMenuLayoutChange = () => {
    const newLayout = menuLayout === 'top' ? 'left' : 'top';
    setMenuLayout(newLayout);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleClick = (event) => {
    setMobileOpen(false);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const logout = (e) => {
    e.preventDefault();
    
    // 모바일 드로어 닫기
    setMobileOpen(false);
    
    dispatch(sessionActions.logout(sessionUser));
    dispatch(menuActions.removeAllTabMenus());
  };
  
  const openProfileAfterMenuRef = useRef(false);

  const handleClickProfile = () => {
    openProfileAfterMenuRef.current = true;
    setAnchorEl(null);
    setMobileOpen(false);
  };

  const handleAccountMenuExited = () => {
    if (!openProfileAfterMenuRef.current) return;
    openProfileAfterMenuRef.current = false;
    const profileMenu = {
      menu: {
        href: "/profile",
        id: "profile",
        title: "마이페이지",
      }
    };

    dispatch(menuActions.setSelectedMenu(profileMenu));
    dispatch(menuActions.addTabMenu(profileMenu.menu));
    navigate("/profile");
  };

  const handleClickInbox = () => {
    setMobileOpen(false);
    setOpenBasket(true);
  }

  // 전체 모듈에서 사용
  // useEffect(
  //   () => {
  //     const gclientId = sessionUser?.id;
  //     if (gclientId) {
  //       const service = '04docu';
  //       // selectSecurityOpenGClientList(service, gclientId);
  //       // selectSecurityCloseGClientList(service, gclientId);
  //       selectOpenedAboutMeGClientList(service, gclientId);
  //     }
  //   }, [sessionUser]
  // )

  const handleClickPaymentHistory = () => {
    setOpenPaymentHistory(true);
  }

  const handleAccordionChange = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]  // 토글 동작으로 변경
    }));
  };

  // 재귀적으로 메뉴를 렌더링하는 함수 (isMobile: 모바일 터치 영역·폰트 확대)
  const renderMenuItems = (menuItems, level = 0, isMobileMenu = false) => {
    const buttonSx = {
      pl: isMobileMenu ? 2.5 : 2,
      minHeight: isMobileMenu ? 48 : undefined,
      fontSize: isMobileMenu ? '1rem' : undefined,
      bgcolor: 'transparent',
      color: '',
      '&:hover': { bgcolor: '#42a5f5', color: '#fff' },
    };
    return menuItems?.map((menu, index) => {
      const isSelected = location.pathname === menu.href;
      const menuId = `${level}-${index}-${menu.title}`;
      const isFirstItem = level === 0 && index === 0;
      
      if (menu.submenus && menu.submenus.length > 0) {
        return (
          <Box key={menuId}>
            <ListItem 
              disablePadding 
              sx={{
                position: 'relative',
                borderLeft: '1px solid #dcdcdc',
                borderRight: '1px solid #dcdcdc',
                borderBottom: '1px solid #dcdcdc',
                borderTop: isFirstItem ? '1px solid #dcdcdc' : 'none',
              }}
            >
              <ListItemButton
                onClick={() => handleAccordionChange(menuId)}
                sx={{
                  ...buttonSx,
                  bgcolor: isSelected ? '#42a5f5' : buttonSx.bgcolor,
                  color: isSelected ? '#fff' : buttonSx.color,
                }}
              >
                <ListItemText
                  primary={<MenuTitleWithBadge title={menu.title} badge={menu.badge} />}
                  primaryTypographyProps={isMobileMenu ? { fontSize: '1rem', component: 'div' } : { component: 'div' }}
                  sx={{ pl: level * 2 }}
                />
                {expandedMenus[menuId] ? <ExpandMoreIcon fontSize="small"/> : <ChevronRightIcon fontSize="small" />}
              </ListItemButton>
            </ListItem>
            <Collapse in={expandedMenus[menuId]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(menu.submenus, level + 1, isMobileMenu)}
              </List>
            </Collapse>
          </Box>
        );
      } else {
        return (
          <ListItem 
            key={menuId} 
            disablePadding
            sx={{ 
              position: 'relative',
              borderLeft: '1px solid #dcdcdc',
              borderRight: '1px solid #dcdcdc',
              borderBottom: '1px solid #dcdcdc',
              borderTop: isFirstItem ? '1px solid #dcdcdc' : 'none',
            }}
          >
            <ListItemButton
              component={RouterLink}
              to={menu.href || '#'}
              onClick={handleDrawerToggle}
              sx={{
                ...buttonSx,
                bgcolor: isSelected ? '#42a5f5' : buttonSx.bgcolor,
                color: isSelected ? '#fff' : buttonSx.color,
              }}
            >
              <ListItemText
                primary={<MenuTitleWithBadge title={menu.title} badge={menu.badge} />}
                primaryTypographyProps={isMobileMenu ? { fontSize: '1rem', component: 'div' } : { component: 'div' }}
                sx={{ pl: level * 2 }}
              />
              <Apps fontSize="small" />
            </ListItemButton>
          </ListItem>
        );
      }
    });
  };
  
  // 모바일 메뉴 렌더링 함수 추가
  const renderMobileMenu = (
    <Drawer
      variant="temporary"
      anchor="left"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        display: { xs: 'block', md: 'none' },
        // AppBar(drawer+1)보다 아래에 두어, 열린 상태에서도 상단 아이콘을 누를 수 있게 한다
        zIndex: (theme) => theme.zIndex.drawer,
        '& .MuiDrawer-paper': { 
          boxSizing: 'border-box', 
          width: 280,
          top: '68.5px',  // AppBar 아래부터 표시
          height: 'calc(100vh - 68.5px)',
        },
      }}
    >
      <Box sx={{ p: 2, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {sessionUser && renderMenuItems(getMobileMenus(menus, sessionUser), 0, true)}
      </Box>
    </Drawer>
  );

  // 상단 메뉴 렌더링 (좌측 메뉴 모드에서는 빈 박스가 헤더 여백을 만들지 않도록 미렌더)
  const renderTopMenu = menuLayout === 'top' && sessionUser ? (
    <Box sx={{
      display: { xs: 'none', md: 'flex' },
      flex: 1,
      minWidth: 0,
      justifyContent: 'flex-start',
      mr: 2,
    }}>
      {menus.map((menu, index) => (
        <CascadingHoverMenus key={index} menu={menu} />
      ))}
    </Box>
  ) : null;

  // 좌측 메뉴 렌더링
  const renderLeftMenu = (
    <Drawer
      variant="permanent"
      sx={{
        display: { 
          xs: 'none', 
          md: ['/glass', '/glass/', '/glass/signin', '/glass/signin/'].includes(location.pathname) ? 'none' : 'block' // 로그인 유형 선택 화면에서는 숨김
        },
        '& .MuiDrawer-paper': { 
          boxSizing: 'border-box', 
          width: 320,
          // position: 'fixed',
          top: '68.5px', // AppBar 높이만큼 여백
          height: 'calc(100% - 68.5px)',
          bgcolor: '#F0F0F0', // 배경색 추가
        },
      }}
    >
      {/* 메뉴 컨텐츠를 감싸는 컨테이너 추가 */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        {/* 메인 메뉴 영역 */}
        <Box sx={{ 
          p: 2,
          flex: 1,
          overflowY: 'auto'
        }}>
          {sessionUser && renderMenuItems(menus)}
        </Box>
        
        {/* 푸터 영역 */}
        <Box sx={{
          p: 2,
          borderTop: '1px solid #dcdcdc',
          bgcolor: '#e0e0e0',
          minHeight: '100px'  // 푸터의 최소 높이 설정
        }}>
          <Box
            sx={{
              // marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Grid container maxWidth="lg">
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12} sm={4} display="flex" alignItems="center">
                    <a href="http://www.flatglass.or.kr" target="_blank" rel="noreferrer">
                      <img src="/kfgwa_footer_logo.png" style={{ width: '70%', height: 'auto' }}/>
                    </a>
                  </Grid>
                  <Grid item xs={12} sm={8} display="flex" alignItems="center">
                    <Typography sx={{ fontSize: '14px', color: '#878787' }}>
                      {"(사)한국판유리창호협회"}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ color: '#878787', mt: 1 }}>
                <Typography sx={{ fontSize: '12px' }}>
                  {"(T) 02-3453-7991, (F) 02-3453-0550"}
                </Typography>
                <Typography sx={{ fontSize: '12px' }}>
                  {"(A) 서울시 마포구 서강로 136, 아이비타워 8층 (노고산동)"}
                </Typography>
                <Typography sx={{ fontSize: '12px' }}>
                  {"copyright© "}{new Date().getFullYear()}{" KFGWA, All RIGHT RESERVED"}
                </Typography>
              </Grid>
              <Grid item xs={12} display="flex" alignItems="center" sx={{ color: '#878787', mt: 1 }}>
                <span style={{ fontSize: '14px' }}>{"CUSTOMER CENTER"}</span>
                <Typography sx={{ fontSize: '14px' }}>
                  <Phone sx={{ verticalAlign: 'middle', ml: 1, mr: 1, fontSize: '14px' }} />
                  {"02-3453-7991"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );

  // 프로모션 추가 : 기간 만료로 주석처리
  // const handleClickEvent = () => {
  //   setOpenPromotion(true);
  // };

  return (
    <>
      {renderMobileMenu}
      <AppBar
        position="fixed" // static에서 fixed로 변경
        // position="static"
        // TODO : style 적용하는 방법(customize) 여러가지가 있으므로 확인할 것
        elevation={0}
        sx={{
          bgcolor: '#F0F0F0',
          color: '#000',
          boxShadow: 'none',
          borderBottom: '1px solid #dcdcdc',
          zIndex: (theme) => theme.zIndex.drawer + 1, // drawer보다 위에 표시
        }}
      >
        {/* <Container maxWidth="xl"> */}
        <Container maxWidth="false">
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '68.5px' }}>
            {/* 모바일: 로그인 화면에서는 햄버거 대신 04DOCU 타이틀 표시 */}
            {(() => {
              const isLoginPath = ['/glass', '/glass/', '/glass/signin', '/glass/signin/', '/glass/signin/gclient', '/glass/signin/gclient/', '/glass/signin/guser', '/glass/signin/guser/'].includes(location.pathname);
              return (
                <>
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 0, display: { xs: isLoginPath ? 'none' : 'flex', md: 'none' } }}
                  >
                    <MenuIcon />
                  </IconButton>
                  {/* 왼쪽 로고 영역 - 모바일에서도 항상 04DOCU 표시 */}
                  <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Grid container sx={{ flexGrow: 1, display: 'flex', mr: { xs: 0, md: 5 } }}>
                <Box sx={{ 
                  fontFamily: 'Pretendard', 
                  letterSpacing: '0pt',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  ml: { xs: 0, md: 1 },
                }}>
                  <Typography component="span" sx={{ 
                    fontWeight: 800,  // ExtraBold
                    fontSize: '2rem',
                  }}>
                    04
                  </Typography>
                  <Typography component="span" sx={{
                    fontWeight: 300,  // Light
                    fontSize: '2rem',
                  }}>
                    DOCU
                  </Typography>
                </Box>
              </Grid>
            </RouterLink>
                </>
              );
            })()}
            {/* 중앙 메뉴 영역 */}
            {renderTopMenu}
            {/* 우측 아이콘 영역 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {/* 프로모션 추가 : 기간 만료로 주석처리 */}
              {/* {sessionUser && (
                <Box
                  component="div"
                  onClick={handleClickEvent}
                  sx={{
                    fontWeight: 'bold',
                    padding: '10px 20px',
                    color: '#ffffff',
                    background: 'linear-gradient(90deg, #ff0000, #FFCCCB, #ee0000, #ff0000)',
                    backgroundSize: '200% 100%',
                    animation: 'flowing 3s ease-out 1s infinite',  // 애니메이션 속도와 지연 시간 조정
                    borderRadius: '4px',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    '@keyframes flowing': {
                      '0%': {
                        backgroundPosition: '100% 0%',  // 시작 위치를 왼쪽으로
                      },
                      '100%': {
                        backgroundPosition: '-100% 0%',  // 끝 위치를 오른쪽으로
                      },
                    }
                  }}
                >
                  이벤트
                </Box>
              )} */}
              {/* 메뉴 레이아웃 변경 버튼 추가 */}
              {sessionUser && (
                <Tooltip title={menuLayout === 'top' ? "좌측 메뉴로 변경" : "상단 메뉴로 변경"}>
                  <IconButton 
                    onClick={handleMenuLayoutChange}
                    sx={{ display: { xs: 'none', md: 'flex' } }}
                  >
                    {menuLayout === 'left' ? <BorderLeft /> : <BorderTop />}
                  </IconButton>
                </Tooltip>
              )}
              {
                sessionUser && showSendBasket && (<Box sx={{ flexGrow: 0 }}>
                  <Tooltip title={"전송바구니"}>
                    <StyledBadge badgeContent={gsendBaskets.length ? gsendBaskets.length : "0"/* 숫자형태의 0이면 나타나지 않음 */} onClick={handleClickInbox} sx={{ cursor: 'pointer' }}>
                      <IconButton onClick={handleClickInbox}>
                        {/* <Inbox color="action" /> */}
                        <AddShoppingCart color="action" />
                      </IconButton>
                    </StyledBadge>
                  </Tooltip>
                </Box>)
              }
              <Box sx={{ flexGrow: 0 }}>
              {
                sessionUser && (
                  <>
                    <Tooltip title="프로필">
                      <IconButton
                        onClick={handleClick}
                        sx={showSendBasket ? { ml: 1 } : undefined}
                        aria-controls={open ? 'account-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        // color="inherit"
                      >
                        <AccountCircle />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={anchorEl}
                      id="account-menu"
                      open={open}
                      onClose={handleClose}
                      onClick={handleClose}
                      TransitionProps={{ onExited: handleAccountMenuExited }}
                      PaperProps={{
                        elevation: 0,
                        sx: {
                          minWidth: '200px', // 메뉴 최소 너비 설정
                          overflow: 'visible',
                          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                          mt: 1.5,
                          '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                          },
                          '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                          },
                        },
                      }}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                      <MenuItem 
                        sx={{ 
                          '&:hover': { backgroundColor: 'transparent' },
                          cursor: 'default',
                          '&.Mui-selected': { backgroundColor: 'transparent' },
                          '&.Mui-focusVisible': { backgroundColor: 'transparent' },
                          p: 2,
                        }}
                        disableRipple
                      >
                        {sessionUser?.sessionUserLoginType === 'USER' ? (
                          <Box>
                            <Typography sx={{ fontSize: '0.9rem' }}>
                              {`${sessionUser?.user?.name} 님`}
                              <Box component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary', ml: 1 }}>
                                {sessionUser?.name}
                              </Box>
                            </Typography>
                          </Box>
                        ) : (
                          <span style={{ fontSize: '0.9rem' }}>{`${sessionUser?.name} 님`}</span>
                        )}
                      </MenuItem>
                      <Divider />
                      <MenuItem
                        component={Link}
                        onClick={handleClickProfile}>
                          <ListItemIcon>
                            <AccountCircle fontSize="small" />
                          </ListItemIcon>
                          <span style={{ fontSize: '0.9rem' }}>{"마이페이지"}</span>
                      </MenuItem>
                      {showPaymentHistory && (
                      <MenuItem
                        component={Link}
                        onClick={handleClickPaymentHistory}
                      >
                        <ListItemIcon>
                          <ReceiptLong fontSize="small" />
                        </ListItemIcon>
                        <span style={{ fontSize: '0.9rem' }}>{"납부이력"}</span>
                      </MenuItem>
                      )}
                      <Divider />
                      {/* TODO : 사용자 계정 임시 주석 : 추후 기능구현해야 함 */}
                      {/* {
                        sessionUser && sessionUser.bizRegNumber && (
                          <MenuItem component={Link} href={"/userAccount"}>
                            <ListItemIcon>
                              <ManageAccounts fontSize="small" />
                            </ListItemIcon>
                            {"사용자 계정"}
                          </MenuItem>
                        )
                      } */}
                      {/* <MenuItem>
                        <ListItemIcon>
                          <Settings fontSize="small" />
                        </ListItemIcon>
                        {"Settings"}
                      </MenuItem> */}
                      {/**
                       * 서버에서 session이 없어진 상태는 error가 설정된 상태이므로(그리고 ErrorPage가 로딩된 상태) 그럴 경우 아래처럼 "/" 이동하면 App에서의 dispatch useEffect에서 restoreUser or restoreSessionUser를 통해 다시 조회하여 signin로 이동
                       * 그렇지 않은 경우 dispatch(sessionActions.logout());를 하면 App의 Route를 통해 signin으로 이동*/}
                      {
                        error ? (
                        <MenuItem component={Link} href={"/"}>
                          <ListItemIcon>
                            <Logout fontSize="small" />
                          </ListItemIcon>
                          <span style={{ fontSize: '0.9rem' }}>{"로그아웃"}</span>
                        </MenuItem>
                        ) : (
                          <MenuItem onClick={logout}>
                            <ListItemIcon>
                              <Logout fontSize="small" />
                            </ListItemIcon>
                            <span style={{ fontSize: '0.9rem' }}>{"로그아웃"}</span>
                          </MenuItem>
                        )
                      }
                    </Menu>
                  </>
                )
              }
              {/* 로그인 화면에서는 AppBar의 로그인 버튼이 보이지 않도록 함 */}
              {
                // TODO : 추후 sessionUser에 따라 signin 위치가 다름
                // sessionUser === undefined && location.pathname !== '/signin' && (
                //   <Button color="inherit" href={"/signin"} startIcon={<Login />}>{"로그인"}</Button>
                // )

                // 회원사 관리자 또는 회원사 사용자 로그인 화면에서 서로간 이동할 수 있는 링크가 있으므로 아래 생략
                // sessionUser === undefined && location.pathname !== '/glass/signin' && (
                //   <Button color="inherit" href={"/glass/signin"} startIcon={<Login />}>
                //     <Typography varaint="body2" sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                //       {"로그인 유형 선택"}
                //     </Typography>
                //   </Button>
                // )
              }
              </Box>
            </Box>
          </Toolbar>
          {showSendBasket && (
            <SendBasketDialog
              open={openBasket}
              setOpen={setOpenBasket}
            />
          )}
          <PaymentHistoryDialog
            open={openPaymentHistory}
            setOpen={setOpenPaymentHistory}
          />
          <PromotionDialog
            open={openPromotion}
            setOpen={setOpenPromotion}
            fromEvent={true}
          />
        </Container>
      </AppBar>

    {/* AppBar 높이만큼 여백 추가 */}
    <Box component="nav" sx={{
      mt: '68.5px',
      width: (
        location.pathname === '/glass' ||
        location.pathname === '/glass/' ||
        location.pathname === '/glass/signin' ||
        location.pathname === '/glass/signin/' ||
        location.pathname === '/glass/signin/gclient' ||
        location.pathname === '/glass/signin/gclient/' ||
        location.pathname === '/glass/signin/guser' ||
        location.pathname === '/glass/signin/guser/' ||
        location.pathname === '/glass/signup/gclient' ||
        location.pathname === '/glass/signup/gclient/' ||
        location.pathname === '/glass/signup/guser' ||
        location.pathname === '/glass/signup/guser/'
      ) ? 0 : 'auto',
    }}>
      {/* 메뉴 레이아웃이 'left'일 때만 좌측 메뉴 표시 */}
      {
        menuLayout === 'left' &&
        location.pathname !== '/glass' &&
        location.pathname !== '/glass/' &&
        location.pathname !== '/glass/signin' &&
        location.pathname !== '/glass/signin/' &&
        location.pathname !== '/glass/signin/gclient' &&
        location.pathname !== '/glass/signin/gclient/' &&
        location.pathname !== '/glass/signin/guser' &&
        location.pathname !== '/glass/signin/guser/' &&
        location.pathname !== '/glass/signup/gclient' &&
        location.pathname !== '/glass/signup/gclient/' &&
        location.pathname !== '/glass/signup/guser' &&
        location.pathname !== '/glass/signup/guser/' &&
        renderLeftMenu
      }
    </Box>
    </>
  )  
}

// TODO : 문제 없는지 모니터링할 것
// export {
//   Navigation,
//   NavigationGuard,
// };
export { Navigation, useMenuLayout };
export default Navigation;